import { AlertCircle, Lock, LogIn, Mail } from 'lucide-react'
import type { JSX } from 'react/jsx-runtime'
import { useNavigate } from 'react-router-dom'
import { routes } from '@/common/constants/routes'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLogin } from '@/features/auth/hooks/use-login'

export const LoginView = (): JSX.Element => {
  const {
    email,
    error,
    handleLogin,
    isLoading,
    password,
    setEmail,
    setPassword,
  } = useLogin()
  const navigate = useNavigate()

  return (
    <div
      className="
        flex min-h-screen items-center justify-center bg-gray-50 p-4
        dark:bg-gray-950
      "
    >
      <Card
        className="
          w-full max-w-md border-none shadow-2xl
          dark:bg-gray-900
        "
      >
        <CardHeader className="space-y-1 text-center">
          <div className="mb-4 flex justify-center">
            <div
              className="
                flex h-12 w-12 items-center justify-center rounded-2xl
                bg-teal-600 text-2xl font-bold text-white shadow-lg
                shadow-teal-100
                dark:shadow-none
              "
            >
              V
            </div>
          </div>
          <CardTitle
            className="
              text-2xl font-black text-gray-900
              dark:text-gray-100
            "
          >
            Welcome Back
          </CardTitle>
          <CardDescription
            className="
              text-[10px] font-bold tracking-widest text-gray-500 uppercase
              dark:text-gray-400
            "
          >
            Log in to your advocate portal
          </CardDescription>
        </CardHeader>
        <form
          onSubmit={(event) => {
            void handleLogin(event)
          }}
        >
          <CardContent className="space-y-4">
            {error && (
              <Alert
                variant="destructive"
                className="
                  rounded-xl border-red-100 bg-red-50
                  dark:bg-red-900/10
                "
              >
                <AlertCircle className="size-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="
                  text-[11px] font-bold tracking-wider text-gray-400 uppercase
                  dark:text-gray-500
                "
              >
                Email
              </Label>
              <div className="relative">
                <Mail
                  className="
                    absolute top-3 left-3 h-4 w-4 text-gray-400
                    dark:text-gray-500
                  "
                />
                <Input
                  required
                  id="email"
                  placeholder="advocate@example.com"
                  type="email"
                  value={email}
                  className="
                    h-11 rounded-xl pl-10
                    dark:border-gray-700 dark:bg-gray-800
                  "
                  onChange={(event) => {
                    setEmail(event.target.value)
                  }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="password"
                  className="
                    text-[11px] font-bold tracking-wider text-gray-400 uppercase
                    dark:text-gray-500
                  "
                >
                  Password
                </Label>
              </div>
              <div className="relative">
                <Lock
                  className="
                    absolute top-3 left-3 h-4 w-4 text-gray-400
                    dark:text-gray-500
                  "
                />
                <Input
                  required
                  id="password"
                  placeholder="••••••••"
                  type="password"
                  value={password}
                  className="
                    h-11 rounded-xl pl-10
                    dark:border-gray-700 dark:bg-gray-800
                  "
                  onChange={(event) => {
                    setPassword(event.target.value)
                  }}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              disabled={isLoading}
              type="submit"
              className="
                h-12 w-full rounded-xl bg-teal-600 font-bold text-white
                shadow-lg shadow-teal-100 transition-all
                hover:bg-teal-700
                active:scale-95
                dark:shadow-none
              "
            >
              {isLoading ? (
                'Logging in...'
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" /> Login
                </>
              )}
            </Button>
            <div
              className="
                text-center text-xs text-gray-500
                dark:text-gray-400
              "
            >
              Don&apos;t have an account?{' '}
              <button
                type="button"
                className="
                  cursor-pointer border-none bg-transparent p-0 font-bold
                  text-teal-600
                  hover:underline
                  dark:text-teal-400
                "
                onClick={() => void navigate(routes.SIGNUP)}
              >
                Sign up
              </button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
